import React, { useState, useRef } from 'react';
import { Send, Bot, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  usePostApiWorkspacesWorkspaceIdConversationsConversationIdMessages,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdMessagesQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey,
} from '@/api/generated/conversations/conversations';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MOCK_CONVERSATION_ID } from '../mockData';
import type { MessageItem, MessageDto, MessageSenderType } from '@/types';
import { cn, extractArrayData } from '@/lib/utils';
import { useAuthStore } from '@/stores/useAuthStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

interface ChatInputProps {
  conversationId: string;
  status: string | number;
  closedAtUtc?: string | null;
  className?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  conversationId,
  status,
  closedAtUtc,
  className,
}) => {
  const [content, setContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();

  const statusStr = String(status).toUpperCase();
  const isAI = statusStr === 'ACTIVE' || statusStr === 'AI_HANDLING' || status === 0;
  const isClosed = statusStr === 'CLOSED' || statusStr === 'RESOLVED' || status === 3;

  const postMessageMutation = usePostApiWorkspacesWorkspaceIdConversationsConversationIdMessages({
    mutation: {
      onMutate: async (newMsg) => {
        // Cancel outgoing queries
        await queryClient.cancelQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdMessagesQueryKey(activeWorkspaceId!, conversationId),
        });

        const clientId = newMsg.data.clientId || `client-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
        newMsg.data.clientId = clientId;

        const now = new Date().toISOString();
        // Optimistically add message
        const optimisticMsg: MessageItem = {
          id: `temp-${Date.now()}`,
          clientId,
          conversationId,
          content: newMsg.data.content || '',
          senderType: 'HumanAgent',
          createdAt: now,
          createdAtUtc: now,
          isPending: true,
          senderId: useAuthStore.getState().user?.id,
        };

        queryClient.setQueriesData<any>(
          { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdMessagesQueryKey(activeWorkspaceId!, conversationId) },
          (old: any) => {
            const oldMessages = extractArrayData<MessageItem>(old);
            return [...oldMessages, optimisticMsg];
          }
        );

        return { optimisticMsg, clientId };
      },
      onSuccess: (data: any, variables, context) => {
        // Backend returns the newly persisted canonical MessageDto
        const canonicalDto: MessageDto | null =
          data && typeof data === 'object' && ('id' in data || 'Id' in data)
            ? (data as MessageDto)
            : null;

        if (canonicalDto) {
          const canonicalMsg: MessageItem = {
            id: canonicalDto.id,
            conversationId,
            content: canonicalDto.content || variables.data.content || '',
            senderType: (canonicalDto.senderType as MessageSenderType) || 'HumanAgent',
            senderName: canonicalDto.senderName || null,
            senderId: canonicalDto.senderId || useAuthStore.getState().user?.id,
            clientId: canonicalDto.clientId || context?.clientId || null,
            payload: canonicalDto.payload,
            toolCallId: canonicalDto.toolCallId,
            toolName: canonicalDto.toolName,
            createdAtUtc: canonicalDto.createdAtUtc,
            createdAt: canonicalDto.createdAtUtc,
            isPending: false,
          };

          // Reconcile cache immediately: replace optimistic message with the canonical DTO
          queryClient.setQueriesData<any>(
            { queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdMessagesQueryKey(activeWorkspaceId!, conversationId) },
            (old: any) => {
              const oldMessages = extractArrayData<MessageItem>(old);

              // 1. If already present by canonical id, update in-place
              const existingIdx = oldMessages.findIndex((m) => m.id === canonicalMsg.id);
              if (existingIdx !== -1) {
                const updated = [...oldMessages];
                updated[existingIdx] = { ...updated[existingIdx], ...canonicalMsg, isPending: false };
                if (Array.isArray(old)) return updated;
                if (old && typeof old === 'object') {
                  if (Array.isArray(old.items)) return { ...old, items: updated };
                  if (Array.isArray(old.data)) return { ...old, data: updated };
                  if (Array.isArray(old.messages)) return { ...old, messages: updated };
                }
                return updated;
              }

              // 2. Replace matching pending optimistic message by clientId or temporary ID
              const pendingIdx = oldMessages.findIndex(
                (m) =>
                  (context?.clientId && m.clientId === context.clientId) ||
                  (context?.optimisticMsg && m.id === context.optimisticMsg.id)
              );

              let newArray: MessageItem[];
              if (pendingIdx !== -1) {
                newArray = [...oldMessages];
                newArray[pendingIdx] = canonicalMsg;
              } else {
                newArray = [...oldMessages, canonicalMsg];
              }

              if (Array.isArray(old)) return newArray;
              if (old && typeof old === 'object') {
                if (Array.isArray(old.items)) return { ...old, items: newArray };
                if (Array.isArray(old.data)) return { ...old, data: newArray };
                if (Array.isArray(old.messages)) return { ...old, messages: newArray };
              }
              return newArray;
            }
          );
        }

        queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) });
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversationId),
        });
      },
      onError: () => {
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdMessagesQueryKey(activeWorkspaceId!, conversationId),
        });
      },
    },
  });

  const handleSend = () => {
    const trimmed = content.trim();
    if (!trimmed || isClosed || postMessageMutation.isPending) return;

    if (conversationId === MOCK_CONVERSATION_ID) {
      toast.info('Esta é uma conversa de exemplo do tour guiado.');
      setContent('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      return;
    }

    const clientId = `client-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

    postMessageMutation.mutate({
      workspaceId: activeWorkspaceId!,
      conversationId,
      data: {
        content: trimmed,
        clientId,
      },
    });

    setContent('');

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    const target = e.target;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 140)}px`;
  };

  return (
    <div className={cn('border-t border-border bg-card p-3 sm:p-4 space-y-2 shrink-0 z-10', className)}>
      {/* Context Information Banners */}
      {isAI && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800/60 text-violet-700 dark:text-violet-300 text-xs">
          <Bot className="h-3.5 w-3.5 shrink-0" />
          <span className="leading-tight">
            O Agente de IA está atendendo esta conversa. Ao enviar uma mensagem, você assumirá o atendimento.
          </span>
        </div>
      )}

      {isClosed && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted text-muted-foreground border border-border text-xs">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          <span>
            Esta conversa foi finalizada e resolvida{closedAtUtc && !isNaN(new Date(closedAtUtc).getTime()) ? ` em ${new Date(closedAtUtc).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}.
          </span>
        </div>
      )}

      {/* Input Composer */}
      <div className="relative flex items-end gap-2">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          disabled={isClosed}
          placeholder={
            isClosed
              ? 'A conversa está resolvida e bloqueada...'
              : 'Digite sua mensagem para o cliente... (Pressione Enter para enviar, Shift+Enter para nova linha)'
          }
          className="min-h-[44px] max-h-[140px] resize-none pr-12 text-xs sm:text-sm py-3 bg-background focus-visible:ring-1"
          rows={1}
        />

        <Button
          type="button"
          size="icon"
          onClick={handleSend}
          disabled={!content.trim() || isClosed || postMessageMutation.isPending}
          className="h-10 w-10 shrink-0 mb-0.5 rounded-lg"
          aria-label="Send message"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default ChatInput;
